import { useState } from 'react'
import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import { Landmark } from 'lucide-react'
import { toast } from 'sonner'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import { LoadingState } from '@/components/patterns/loading-state'
import { StripeConnectDeferredRouteContent } from '@/components/stripe-connect/StripeConnectFallback'
import StripeConnectOnboardingManager from '@/components/stripe-connect/StripeConnectOnboardingManager'
import StripeConnectAlertsPanel from '@/components/stripe-connect/StripeConnectAlertsPanel'
import StripeMigrationCompleteDialog from '@/components/stripe-connect/StripeMigrationCompleteDialog'
import StripeMigrationProgressNotice from '@/components/stripe-connect/StripeMigrationProgressNotice'
import StripeMigrationRecovery from '@/components/stripe-connect/StripeMigrationRecovery'
import { useStripeConnectRouteReady } from '@/components/stripe-connect/useStripeConnectRouteReady'
import { Button } from '@/components/ui/button'
import { Can } from '@/lib/casl'
import { stripeHubLink } from '@/lib/stripe-financial-routes'
import {
  stripeMigrationQueries,
  useStripeMigration,
  useStripeMigrationAction,
} from '@/lib/services/stripe-connect/stripe-migration.query'
import { assertNotSalesDemoOrganization } from '@/lib/services/sales-demos/sales-demos.guard'
import { getErrorMessage } from '@/lib/utils'
import {
  isStripeUpgradeComplete,
  recoveryControls,
} from '@/lib/services/stripe-connect/stripe-migration.schema'

export const Route = createFileRoute('/_authenticated/$orgId/settings/stripe/upgrade')({
  component: StripeUpgradePage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('edit', 'stripe-account')) throw notFound()
    await assertNotSalesDemoOrganization(context.queryClient, params.orgId)
    const migration = await context.queryClient.fetchQuery(
      stripeMigrationQueries.status({ organizationId: params.orgId, polling: 'upgrade' })
    )
    if (!migration.available && !migration.started) throw notFound()
    if (isStripeUpgradeComplete(migration)) {
      throw redirect(stripeHubLink(params.orgId))
    }
    return { isReadOnly: false }
  },
})

/** @param {{ orgId: string, isRecoveryStaff: boolean }} props */
function StripeUpgradeContent({ orgId, isRecoveryStaff }) {
  const [resume, setResume] = useState(false)
  const [exited, setExited] = useState(false)
  const { isReady: isOnboardingReady, setReady: setOnboardingReady } = useStripeConnectRouteReady()
  const {
    data: migration,
    isPending,
    isError,
    refetch,
  } = useStripeMigration(orgId, {
    polling: 'upgrade',
  })
  const start = useStripeMigrationAction(orgId, 'start')
  const sync = useStripeMigrationAction(orgId, 'sync')
  /** @param {{ mutateAsync: () => Promise<unknown> }} mutation */
  const run = async (mutation) => {
    try {
      await mutation.mutateAsync()
    } catch (error) {
      toast.error('Could not update account setup', { description: getErrorMessage(error) })
    }
  }
  if (isPending) return <LoadingState label="Loading account setup…" />
  if (isError && !migration)
    return (
      <InfoNotice
        title="Account setup is unavailable"
        tone="warning"
        action={<Button onClick={() => refetch()}>Try again</Button>}
      >
        Your payment routing has not changed.
      </InfoNotice>
    )
  const completed = migration.onboarding_complete
  const upgradeComplete = isStripeUpgradeComplete(migration)
  const recoveryStep = migration.recovery ? recoveryControls(migration.recovery).step : null
  const needsAccountAccess =
    migration.target_disconnected || (isRecoveryStaff && recoveryStep === 'account-access')
  const canRetry = isRecoveryStaff && migration.recovery?.can_retry === true
  const showOnboarding =
    migration.can_onboard &&
    !needsAccountAccess &&
    !migration.activated &&
    ((!completed && !exited) || resume)
  const isPreparingAccount =
    migration.started &&
    !completed &&
    !exited &&
    !migration.can_onboard &&
    !needsAccountAccess &&
    !canRetry
  // Keep the target surface mounted behind the completion dialog until navigation.
  const showStatus = migration.started && !showOnboarding && !isPreparingAccount && !canRetry
  const showRecovery =
    isRecoveryStaff &&
    !canRetry &&
    !upgradeComplete &&
    migration.recovery &&
    (migration.activated ||
      migration.recovery.import ||
      !['account-readiness', 'checking', 'complete'].includes(recoveryStep))
  return (
    <StripeConnectDeferredRouteContent isReady={!showOnboarding || isOnboardingReady}>
      <div className="space-y-6 pb-6">
        <AdminPageHeader
          icon={Landmark}
          title={completed || exited ? 'Stripe account upgrade' : 'Upgrade your Stripe account'}
          description={
            completed || exited
              ? migration.activated
                ? 'The upgraded account is now in use.'
                : 'Track your upgrade and review any follow-up requests from Stripe.'
              : 'Review your account details securely with Stripe.'
          }
        />
        {isError ? (
          <InfoNotice title="Status updates are reconnecting" tone="warning" compact>
            Your progress is saved. We’ll retry automatically; you can keep this page open.
          </InfoNotice>
        ) : null}
        {canRetry ? (
          <InfoNotice
            title="Account setup needs attention"
            tone="warning"
            action={
              <Button disabled={sync.isPending} onClick={() => run(sync)}>
                {sync.isPending ? 'Retrying…' : 'Retry setup'}
              </Button>
            }
          >
            {migration.recovery.last_error === 'account_mismatch'
              ? 'The original and new accounts could not be matched. Resolve the account mismatch, then retry setup.'
              : 'Stripe rejected a setup request. Correct the reported account details or access issue, then retry setup.'}
          </InfoNotice>
        ) : null}
        {isPreparingAccount ? <LoadingState label="Preparing your new Stripe account…" /> : null}
        {!migration.started ? (
          <InfoNotice
            title="Get ready for new features"
            action={
              <Button disabled={start.isPending} onClick={() => run(start)}>
                Upgrade account
              </Button>
            }
          >
            Keep accepting payments while you complete setup. Sign in to Stripe to reuse eligible
            verified business details.
          </InfoNotice>
        ) : null}
        {showStatus && !migration.activated ? (
          <StripeMigrationProgressNotice
            migration={migration}
            requiresAction={migration.requires_action}
            action={
              !completed && migration.can_onboard && !needsAccountAccess ? (
                <Button variant="outline" size="sm" onClick={() => setResume(true)}>
                  Continue setup
                </Button>
              ) : null
            }
          />
        ) : null}
        {showStatus && completed && migration.can_onboard ? (
          <StripeConnectAlertsPanel
            organizationId={orgId}
            accountContext="migration-target"
            title="New Stripe Account Status"
            description={
              migration.activated
                ? 'The upgraded account is now handling new payments.'
                : 'Review any information Stripe needs for your new account. Your existing account continues to handle payments until the upgrade is complete.'
            }
            showInitialPlaceholder
            noActionsNeededStateMode="resolved"
          />
        ) : null}
        {showOnboarding ? (
          <StripeConnectOnboardingManager
            organizationId={orgId}
            accountContext="migration-target"
            title="Complete your account upgrade"
            description="Sign in to Stripe to review and reuse eligible verified details."
            onReadyChange={setOnboardingReady}
            onExit={() => {
              setExited(true)
              setResume(false)
              void run(sync)
            }}
          />
        ) : null}
        {showRecovery &&
        (completed || migration.activated || needsAccountAccess) &&
        !showOnboarding ? (
          <StripeMigrationRecovery
            organizationId={orgId}
            recovery={migration.recovery}
            onReviewDetails={
              migration.can_onboard && !migration.activated ? () => setResume(true) : undefined
            }
          />
        ) : null}
        {upgradeComplete ? (
          <StripeMigrationCompleteDialog organizationId={orgId} isRecoveryStaff={isRecoveryStaff} />
        ) : null}
      </div>
    </StripeConnectDeferredRouteContent>
  )
}

function StripeUpgradePage() {
  const { orgId } = Route.useParams()
  return (
    <Can I="access" a="global" passThrough>
      {(isGlobal) => (
        <Can I="manage" a="stripe-migrations" passThrough>
          {(canManage) => (
            <StripeUpgradeContent
              key={orgId}
              orgId={orgId}
              isRecoveryStaff={isGlobal && canManage}
            />
          )}
        </Can>
      )}
    </Can>
  )
}
